/**
 * @file 参考書ルートのクライアント。
 *
 * @what: `GET /families/{familyId}/study/textbook_routes` の取得と、最新の改訂版を選ぶ判断を担う。
 * @why: 参考書ルートは家族が取り込むデータで、アプリには同梱しない (epic_23 §5)。科目や区分の
 *   語彙は出典ごとに違うのでサーバは意味も順序も持たず、並びは受け取った順をそのまま使う。
 * @failure: ApiError を伝播する。空配列は「まだ何も取り込んでいない」で、正常系として扱う。
 */

import { apiFetch } from "@/shared/api/client";

/**
 * 本の役割を表す。
 *
 * primary = 区分の推奨 / alternative = 推奨の代わりに使ってもよい本 /
 * advanced = 推奨を仕上げたあとに足す上位の本 / supplement = 出典が用意する補助教材
 */
export type StudyTextbookRouteRole = "primary" | "alternative" | "advanced" | "supplement";

export const STUDY_TEXTBOOK_ROUTE_ROLE_LABELS: Record<StudyTextbookRouteRole, string> = {
  primary: "おすすめ",
  alternative: "代替可",
  advanced: "余力あれば",
  supplement: "補助教材",
};

export interface StudyTextbookRouteLevel {
  /** 出典が定めた習得レベル ("星3個" など)。 */
  label: string;
  /** そこまで仕上げたときの到達の目安 (route.scale の目盛り。偏差値なら "65")。 */
  target?: string;
  /** 到達偏差値の数値で、表の行を決める。得点率など偏差値でない目安は持たない。 */
  deviation?: number;
}

/**
 * 物差しの目盛り1つを表す。
 *
 * 偏差値は数値そのものが意味を持つが、資格の試験の区分や教養の段は数値に意味が無く順番だけを
 * 持つ。目盛りに名前を付けられるようにして、同じ器で両方を扱う。
 */
export interface StudyTextbookRouteScaleMark {
  /** 物差しの上の位置 (本の difficulty と同じ物差し)。 */
  value: number;
  /** 画面に出す目盛りの名前 ("学科一般"・"初級")。 */
  label: string;
  /** 値の出どころを書く。 */
  note?: string;
}

/**
 * 試験の先にある働き方1つを表す。
 *
 * 資格の平均年収だけでは、その資格でどう働けるかが見えない。おとなの学習者のときだけ画面に出す。
 */
export interface StudyTextbookRouteCareer {
  name: string;
  /** 資格をどう活かすかを表す (requirement = 法律上の要件 / knowledge = 知識 / title = 肩書)。 */
  use: "requirement" | "knowledge" | "title";
  /** 試験の先に必要なもの (実務経験・別の資格・事業の許可・講習) を書く。 */
  prerequisite?: string;
  /** 年収の幅 (万円)。出せない働き方があるので、どちらも無いことがある。 */
  income_from?: number;
  income_to?: number;
  /** 年収の出どころ (掲載額・有価証券報告書・統計・体験談) を書く。 */
  income_source?: string;
  /** 雇用の形と需要の向きを書く。 */
  stability?: string;
  /** 肉体と精神の負荷 (1=低 / 2=中 / 3=高)。無ければ未設定とする。 */
  load_physical?: number;
  load_mental?: number;
  /** 前の働き方からどう移るかを書く。 */
  path?: string;
  source_url?: string;
}

/** 物差し (route.scale) の上の目標の印を表す。受験では志望校、資格と教養では目標の段になる。 */
export interface StudyTextbookRouteSchool {
  name: string;
  deviation: number;
  /** 年度や回など、値の出どころ。 */
  note?: string;
}

export interface StudyTextbookRouteBook {
  title: string;
  publisher?: string;
  /** 表紙を引くキー。無ければ頭文字の枠になる。 */
  isbn?: string;
  /** 商品ページ。 */
  url?: string;
  /** 出典の解説記事の URL を持つ。 */
  source_url?: string;
  role: StudyTextbookRouteRole;
  /** 使う条件・巻の構成など。 */
  note?: string;
  levels: StudyTextbookRouteLevel[];
  /** 本の量 (問題数・語数・ページ数など) で、無ければ不明とする。 */
  volume?: number;
  /** volume の単位 ("問" "語" "テーマ" "長文" "ページ" "文")。 */
  volume_unit?: string;
  /** 1単位にかける分の目安で、無ければ単位ごとの既定値を使う。 */
  minutes_per_unit?: number;
  /** この本の既定の周回数 (網羅系は3、講義系の参考書と過去問は1、など)。無ければ進め方ごとの既定値を使う。 */
  rounds?: number;
  /** この本が特定の志望校のためのもの (過去問など) であるときの校名を持つ。空なら全員向けになる。 */
  school?: string;
  /**
   * この本の水準 (使い始める人の偏差値の目安)。levels[].deviation の到達の目安とは別で、表の行は
   * この値で決める。無ければ画面は到達の目安で並べる (古いルート)。
   */
  difficulty?: number;
  /** 使い続ける上限の偏差値を持つ。無ければ difficulty と同じになる。 */
  difficulty_to?: number;
  /** 水準の出どころ (武田塾の参考書ルートのレベル、出版社の対象の表記、など)。 */
  difficulty_source?: string;
}

/**
 * 他の区分との進め方を表す。parallel = 並行して進める (単語帳と長文など) / in_turn = 区分の順に
 * 進める (基礎固め → 典型問題 → 実戦)。無ければ parallel とみなす。
 */
export type StudyTextbookRouteFlow = "parallel" | "in_turn";

/**
 * 課題がどの段に届いたかを決める基準1つを表す。
 *
 * 基準を課題ごとに持つのは、同じ段に求めることが課題で違うためである (道は場所と手段を伝える、
 * 写真は承諾と構図の確認で足りる)。
 */
export interface StudyTextbookRouteTaskCriterion {
  /** 物差しの目盛りの値が入る。 */
  mark: number;
  /** その段に届くために満たすことを書く。 */
  must: string;
}

/**
 * 区分の中の課題1つ (「道を聞かれて答える」) を表す。
 *
 * 技能のカリキュラム (英会話・イラスト) だけが持つ。本を読んでも身につかない技能のために、場面を
 * 決めて練習し、その場で評価する単位である。
 */
export interface StudyTextbookRouteTask {
  id: string;
  label: string;
  /** 場面を説明する。AI が相手役を演じる材料になる。 */
  scene: string;
  /** AI が演じる役を書く。 */
  role: string;
  /** 相手役の最初の一言が入る。空なら AI が場面から作る。 */
  opening?: string;
  criteria: StudyTextbookRouteTaskCriterion[];
  /** 本と同じ物差しの上で、使い始める水準を置く。 */
  difficulty?: number;
  /** 使い終える水準を置く。 */
  difficulty_to?: number;
  /** 1回にかかる分を置く。 */
  minutes?: number;
  rounds?: number;
}

export interface StudyTextbookRouteTrack {
  id: string;
  label: string;
  /** 区分全体への注記 (使う条件・注意点)。 */
  note?: string;
  flow?: StudyTextbookRouteFlow;
  /** この区分を試験日の何か月前から始めるか (中学入試の「小4の春」は34)。無ければ未設定になる。 */
  start_months_before_exam?: number;
  /** 始める時期の表示名 (例 "小4の春")。 */
  start_label?: string;
  /** 出典の並び順のまま (推奨が先)。 */
  books: StudyTextbookRouteBook[];
  /** 課題で、技能のカリキュラムだけが持つ。 */
  tasks?: StudyTextbookRouteTask[];
}

export interface StudyTextbookRouteSubject {
  id: string;
  label: string;
  tracks: StudyTextbookRouteTrack[];
}

export interface StudyTextbookRoute {
  id: string;
  schema_v: number;
  /** 出典を束ねる id で、同じ series_id の中で order 最大を最新の改訂版とみなす。 */
  series_id: string;
  order: number;
  title: string;
  /** 受験の対象 ("大学入試（難関大・医学部）" など)。 */
  target: string;
  source_name: string;
  source_url?: string;
  edition?: string;
  /** 本の levels[].target が何の目盛りか ("全統記述模試の偏差値"・"試験の区分" など)。 */
  scale?: string;
  /** 目盛りの定義で、空なら読み手は数値をそのまま目盛りにする。 */
  scale_marks?: StudyTextbookRouteScaleMark[];
  /** 目盛りの刻みで、無ければ偏差値の5刻みを使う。 */
  scale_step?: number;
  /** 試験を受ける学年の通し番号 (小1=1 … 小6=6、中1=7 … 中3=9、高1=10 … 高3=12)。無ければ未設定になる。 */
  exam_grade?: number;
  /** 試験日の月日 ("02-01")。exam_grade と組で生年月日から試験日を出す。 */
  exam_month_day?: string;
  /** 目標の印で、古いルートには無い。 */
  schools?: StudyTextbookRouteSchool[];
  /** 試験の先の働き方で、資格のルートだけが持つ。 */
  careers?: StudyTextbookRouteCareer[];
  subjects: StudyTextbookRouteSubject[];
  created_at: string;
  updated_at: string;
}

export async function fetchTextbookRoutes(
  familyId: string,
  signal?: AbortSignal,
): Promise<StudyTextbookRoute[]> {
  const res = await apiFetch<{ routes: StudyTextbookRoute[] }>(
    `/families/${encodeURIComponent(familyId)}/study/textbook_routes`,
    signal ? { signal } : {},
  );
  return res.routes;
}

/**
 * 出典ごとに最新の改訂版だけを残す。
 *
 * @what: 同じ `series_id` の中で `order` が最大のものを選ぶ。`order` が同点なら id の
 *   辞書順で後ろのものを採る (順序を決める根拠が無いので、どちらを採るかを固定するだけ)。
 *   返す並びは受け取った順 (id 昇順) のまま。
 * @why: 年度が変わっても古いものを消さない運用なので、画面は最新だけを出す (よむ と同じ)。
 */
export function latestTextbookRoutes(routes: readonly StudyTextbookRoute[]): StudyTextbookRoute[] {
  const best = new Map<string, StudyTextbookRoute>();
  for (const r of routes) {
    const cur = best.get(r.series_id);
    if (!cur || r.order > cur.order || (r.order === cur.order && r.id > cur.id)) {
      best.set(r.series_id, r);
    }
  }
  return routes.filter((r) => best.get(r.series_id) === r);
}

/**
 * 受験の対象ごとにルートをまとめる (出現順)。
 *
 * @why: 大学入試の理系編と文系編のように、同じ対象に複数のルートが並ぶ。画面はまず対象で
 *   束ね、その中でルートを切り替える。
 */
export function groupRoutesByTarget(
  routes: readonly StudyTextbookRoute[],
): { target: string; routes: StudyTextbookRoute[] }[] {
  const groups: { target: string; routes: StudyTextbookRoute[] }[] = [];
  for (const r of routes) {
    const g = groups.find((x) => x.target === r.target);
    if (g) g.routes.push(r);
    else groups.push({ target: r.target, routes: [r] });
  }
  return groups;
}

/** ルートの冊数 (役割を問わず数える)。 */
export function countRouteBooks(route: StudyTextbookRoute): number {
  let n = 0;
  for (const s of route.subjects) for (const t of s.tracks) n += t.books.length;
  return n;
}
